// Sentence Structure — word order, ser vs estar, questions, negation,
// prepositions, contractions. Ported from the legacy views.structure; uses
// the shared renderGuideSections helper.
import { renderGuideSections } from "../ui/guideSections.js";

// Hand-drawn flat SVG of the S–V–O flow — accurate and editable because it's
// vector code, not a generated image.
const WORD_ORDER_FIGURE = `
  <div class="bg-white border border-sage-100 rounded-2xl p-4 sm:p-5 mb-6">
    <p class="text-xs font-semibold uppercase tracking-wide text-sage-400 mb-2">Word order at a glance</p>
    <svg viewBox="0 0 660 230" class="w-full h-auto max-w-2xl mx-auto" role="img" aria-label="Portuguese word order: Subject then Verb then Object">
      <defs>
        <marker id="wo-arrow" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0 0 L8 3 L0 6 z" fill="#94a3b8"></path>
        </marker>
      </defs>
      <rect x="30" y="28" width="160" height="58" rx="14" fill="#d1fae5"></rect>
      <text x="110" y="63" text-anchor="middle" font-weight="700" fill="#065f46" font-size="17">SUBJECT</text>
      <rect x="250" y="28" width="160" height="58" rx="14" fill="#fef3c7"></rect>
      <text x="330" y="63" text-anchor="middle" font-weight="700" fill="#92400e" font-size="17">VERB</text>
      <rect x="470" y="28" width="160" height="58" rx="14" fill="#e0f2fe"></rect>
      <text x="550" y="63" text-anchor="middle" font-weight="700" fill="#075985" font-size="17">OBJECT</text>
      <path d="M190 57 H250" stroke="#94a3b8" stroke-width="3" marker-end="url(#wo-arrow)"></path>
      <path d="M410 57 H470" stroke="#94a3b8" stroke-width="3" marker-end="url(#wo-arrow)"></path>
      <text x="110" y="138" text-anchor="middle" fill="#1f2937" font-size="20" font-weight="600">Maria</text>
      <text x="330" y="138" text-anchor="middle" fill="#1f2937" font-size="20" font-weight="600">come</text>
      <text x="550" y="138" text-anchor="middle" fill="#1f2937" font-size="20" font-weight="600">maçãs</text>
      <text x="110" y="160" text-anchor="middle" fill="#9ca3af" font-size="12">Maria</text>
      <text x="330" y="160" text-anchor="middle" fill="#9ca3af" font-size="12">eats</text>
      <text x="550" y="160" text-anchor="middle" fill="#9ca3af" font-size="12">apples</text>
      <text x="330" y="206" text-anchor="middle" fill="#6b7280" font-size="13">Default order is Subject → Verb → Object — the same as English.</text>
    </svg>
    <p class="text-sage-600 text-sm mt-2">Because the verb ending already shows <i>who</i> is acting, Portuguese can shuffle parts for emphasis — but <b>S–V–O</b> is the safe default. (Adjectives, though, go <b>after</b> the noun: <i>uma casa <b>branca</b></i>.)</p>
  </div>`;

export function structureView(app) {
  renderGuideSections(
    app,
    "Sentence Structure",
    "How Portuguese sentences are built — word order, the two verbs for \"to be\", questions and negation, prepositions, and the contractions that fuse prepositions with articles.",
    [
      {
        title: "1. The big picture — four shifts from English",
        body: `
          <p class="text-sage-700 mb-2">Good news for an English speaker: the basic order is the same — <b>subject → verb → object</b>. The differences come down to a short list, and most of them make Portuguese <i>simpler</i>, not harder.</p>
          <ol class="text-sage-700 text-sm space-y-1 list-decimal pl-5">
            <li>The subject is usually <b>dropped</b> — the verb ending already says who.</li>
            <li>Adjectives usually go <b>after</b> the noun.</li>
            <li>There's <b>no "do/does"</b> helper for questions or negatives — the plain verb does the job.</li>
            <li>Questions are made by <b>intonation</b> alone — no inverted ¿ and no word reordering.</li>
          </ol>
          <p class="text-sage-700 text-sm mt-3"><b>Don't translate "do":</b> <i>Trabalhas?</i> already means "Do you work?" — there's no word for the question/negative <i>do</i>. The verb alone asks: <i>Ela fala inglês?</i> = "Does she speak English?"</p>
        `,
      },
      {
        title: "2. Basic word order",
        body: `
          <p class="text-sage-700 mb-2">Default order is <b>Subject – Verb – Object</b>, but it's looser than in English because verb endings already show who's doing what.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>(Eu) como maçãs.</i> — I eat apples. (Subject often dropped.)</li>
            <li><i>Apetece-me um café.</i> — Literally: "A coffee appeals to me." The grammatical subject (<i>um café</i>) comes after the verb.</li>
            <li>Adjectives go <b>after</b> the noun: <i>uma casa <b>branca</b></i>.</li>
            <li>Object pronouns attach <b>after</b> the verb by default: <i>Vejo-o.</i> — I see him/it; but they move <b>before</b> after <i>não/que</i>: <i>Não o vejo.</i></li>
            <li>With an infinitive, the pronoun can attach to it: <i>quero vê-lo</i>, <i>vou fazê-lo</i>.</li>
          </ul>
        `,
      },
      {
        title: "3. Ser vs Estar — two verbs for \"to be\"",
        body: `
          <p class="text-sage-700 mb-2">Portuguese splits English "to be" into two verbs:</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-3">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Use SER for…</th><th class="py-2 px-3">Use ESTAR for…</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Identity / profession (<i>sou médico</i>)</td><td class="py-2 px-3">Location (<i>estou em casa</i>)</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Origin / nationality (<i>é de Portugal</i>)</td><td class="py-2 px-3">Temporary states (<i>estou cansado</i>)</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Permanent traits (<i>é alto</i>)</td><td class="py-2 px-3">Moods / feelings (<i>está feliz hoje</i>)</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Time / dates (<i>são três horas</i>)</td><td class="py-2 px-3">Ongoing actions (<i>está a comer</i>)</td></tr>
              <tr><td class="py-2 pr-3">Material / possession (<i>é de madeira</i>)</td><td class="py-2 px-3">Results of change (<i>está partido</i>)</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm"><b>Meaning shifts:</b> <i>ser aborrecido</i> = to be boring · <i>estar aborrecido</i> = to be bored. <i>ser bom</i> = to be good (a good person) · <i>estar bom</i> = to be well / taste good.</p>
        `,
      },
      {
        title: "4. Questions",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Yes/no questions just flip intonation — no extra word needed. Written: <i>Falas português?</i></li>
            <li>There is <b>no opening</b> ¿ or ¡ — just a final <b>?</b> or <b>!</b></li>
            <li><b>Common question words:</b> que / o quê, quem, quando, onde, como, qual, quanto, porquê.</li>
            <li>Examples: <i>Que fazes? Onde vives? Quantos anos tens? Porque não vens?</i></li>
            <li><b>que</b> vs <b>qual</b>: use <i>que</i> for definitions; <i>qual</i> for choices. <i>O que é isto?</i> vs <i>Qual preferes?</i></li>
          </ul>
        `,
      },
      {
        title: "5. Negation",
        body: `
          <p class="text-sage-700 mb-2">Put <b>não</b> directly before the verb: <i>Não falo francês.</i></p>
          <p class="text-sage-700 mb-2">Unlike English, Portuguese uses <b>double negatives</b> — they don't cancel each other out:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Não tenho <b>nada</b>.</i> — I don't have anything.</li>
            <li><i>Não conheço <b>ninguém</b>.</i> — I don't know anyone.</li>
            <li><i><b>Nunca</b> vou</i> OR <i>Não vou <b>nunca</b></i> — I never go. (Either is fine.)</li>
            <li>If the negative word comes <b>before</b> the verb, drop the <i>não</i>; if it comes after, keep it.</li>
          </ul>
        `,
      },
      {
        title: "6. Common prepositions",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>a</b> — to, at; often contracts: a + o = ao, a + a = à.</li>
            <li><b>de</b> — of, from; possession (<i>o livro <b>do</b> João</i>).</li>
            <li><b>em</b> — in, on, at; contracts: em + o = no, em + a = na.</li>
            <li><b>com / sem</b> — with / without.</li>
            <li><b>por</b> — because of, by, through, in exchange for; contracts: por + o = pelo.</li>
            <li><b>para</b> — for, in order to, destined for (goal/recipient).</li>
            <li><b>desde / até</b> — from / until (time or space).</li>
            <li><b>entre</b> — between, among.</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>Contractions:</b> prepositions routinely fuse with the article — <i>de + o = do</i>, <i>em + a = na</i>, <i>a + o = ao</i>, <i>por + os = pelos</i>.</p>
        `,
      },
      {
        title: "7. Contractions — prepositions + articles",
        body: `
          <p class="text-sage-700 mb-2">Portuguese fuses prepositions with the following article almost everywhere — these are mandatory:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>de + o/a → do/da</b>: <i>o carro <b>do</b> professor.</i></li>
            <li><b>em + o/a → no/na</b>: <i>Estou <b>na</b> escola.</i></li>
            <li><b>a + o/a → ao/à</b>: <i>Vou <b>ao</b> mercado.</i></li>
            <li><b>por + o/a → pelo/pela</b>: <i>passo <b>pelo</b> jardim.</i></li>
          </ul>
          <p class="text-sage-700 text-sm mt-2">They also fuse with indefinite articles and demonstratives: <i>em + um = num</i>, <i>de + este = deste</i>, <i>de + aquele = daquele</i>.</p>
        `,
      },
    ],
    WORD_ORDER_FIGURE
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
